import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { ForemanGroup, ForemanGroupCreate, ForemanGroupUpdate, QueryParams } from "../types";

const ForemanGroupManagement: React.FC = () => {
  const [foremanGroups, setForemanGroups] = useState<ForemanGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedForemanGroup, setSelectedForemanGroup] = useState<ForemanGroup | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState<ForemanGroupCreate>({
    name: "",
    type: "",
    code: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const toast = useToast();

  // Fetch foreman groups data
  const fetchForemanGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: QueryParams = {
        search: searchTerm || undefined,
        include_disabled: includeDisabled,
      };
      const response = await adminApiService.getForemanGroups(params);
      setForemanGroups(response.data);
    } catch (error: any) {
      setError("Failed to fetch foreman groups");
      console.error("Error fetching foreman groups:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForemanGroups();
  }, [searchTerm, includeDisabled]);

  const handleOpenModal = (foremanGroup?: ForemanGroup) => {
    if (foremanGroup) {
      setSelectedForemanGroup(foremanGroup);
      setFormData({
        name: foremanGroup.name,
        type: foremanGroup.type || "",
        code: foremanGroup.code,
      });
      setIsEditing(true);
    } else {
      setSelectedForemanGroup(null);
      setFormData({
        name: "",
        type: "",
        code: "",
      });
      setIsEditing(false);
    }
    onOpen();
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast({
        title: "Validation Error",
        description: "Name and Code are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitLoading(true);
    try {
      if (isEditing && selectedForemanGroup) {
        const updateData: ForemanGroupUpdate = {
          name: formData.name,
          type: formData.type || undefined,
          code: formData.code,
        };
        await adminApiService.updateForemanGroup(selectedForemanGroup.id, updateData);
        toast({
          title: "Success",
          description: "Foreman group updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await adminApiService.createForemanGroup(formData);
        toast({
          title: "Success",
          description: "Foreman group created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      fetchForemanGroups();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? "update" : "create"} foreman group: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting foreman group:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (foremanGroup: ForemanGroup) => {
    try {
      await adminApiService.deleteForemanGroup(foremanGroup.id);
      toast({
        title: "Success",
        description: "Foreman group deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchForemanGroups();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete foreman group: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error deleting foreman group:", error);
    }
  };

  const handleRestore = async (foremanGroup: ForemanGroup) => {
    try {
      await adminApiService.restoreForemanGroup(foremanGroup.id);
      toast({
        title: "Success",
        description: "Foreman group restored successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchForemanGroups();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to restore foreman group: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error restoring foreman group:", error);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading foreman groups...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Foreman Group Management</Heading>
          
          <HStack spacing={4} mb={4}>
            <Input
              placeholder="Search foreman groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxW="300px"
            />
            <Select
              value={includeDisabled ? "all" : "active"}
              onChange={(e) => setIncludeDisabled(e.target.value === "all")}
              maxW="200px"
            >
              <option value="active">Active Only</option>
              <option value="all">Include Disabled</option>
            </Select>
            <Button colorScheme="blue" onClick={() => handleOpenModal()}>
              Add Foreman Group
            </Button>
          </HStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Group Name</Th>
                <Th>Code</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {foremanGroups.map((foremanGroup) => (
                <Tr key={foremanGroup.id}>
                  <Td>{foremanGroup.name}</Td>
                  <Td>
                    <Badge colorScheme="blue" variant="solid">
                      {foremanGroup.code}
                    </Badge>
                  </Td>
                  <Td>
                    {foremanGroup.type ? (
                      <Badge colorScheme="purple" variant="outline">
                        {foremanGroup.type}
                      </Badge>
                    ) : (
                      <Text color="gray.500">-</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={foremanGroup.is_deleted ? "red" : "green"}>
                      {foremanGroup.is_deleted ? "Deleted" : "Active"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(foremanGroup)}
                      />
                      {!foremanGroup.is_deleted ? (
                        <>
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenModal(foremanGroup)}
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => handleDelete(foremanGroup)}
                          />
                        </>
                      ) : (
                        <IconButton
                          aria-label="Restore"
                          icon={<RepeatIcon />}
                          size="sm"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => handleRestore(foremanGroup)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {foremanGroups.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No foreman groups found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Foreman Group Form Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? "Edit Foreman Group" : "Add New Foreman Group"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Group Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter group name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Group Code</FormLabel>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="Enter group code"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Type</FormLabel>
                <Input
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  placeholder="Enter type (optional)"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={submitLoading}
              loadingText={isEditing ? "Updating..." : "Creating..."}
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ForemanGroupManagement;
